// src/realtime/notificationsSocket.ts
// ==========================================================
//    Notifications Realtime Socket
// ==========================================================

import websocketManager from "../lib/websocketManager";
import { logger } from "../utils/logging/logger";
import type { NotificationDTO } from "../types/notifications/notificationsTypes";

type NotificationHandler = (payload: NotificationDTO) => void;

let notificationListenerBound = false;

// -----------------------------------------------------------------------------
// Ensure websocket connection
// -----------------------------------------------------------------------------
export function ensureNotificationsSocketConnected(token: string) {
  try {
    websocketManager.connect(token, "notifications");
  } catch (err) {
    logger.error("[notificationsSocket] Failed to connect WS", err);
  }
}

// -----------------------------------------------------------------------------
// Listen for notification events
// -----------------------------------------------------------------------------
export function onNotification(handler: NotificationHandler) {
  if (notificationListenerBound) return;
  notificationListenerBound = true;

  const cb = (data: any) => {
    handler(data as NotificationDTO);
  };

  websocketManager.on("notifications:notification", cb);

  return () => {
    websocketManager.off("notifications:notification", cb);
    notificationListenerBound = false;
  };
}

// -----------------------------------------------------------------------------
// Optional listener for notifications ready event
// -----------------------------------------------------------------------------
export function onNotificationsReady(handler: (data: { status?: string }) => void) {
  const cb = (data: any) => handler(data ?? {});
  websocketManager.on("notifications:ready", cb);

  return () => {
    websocketManager.off("notifications:ready", cb);
  };
}

// -----------------------------------------------------------------------------
// Optional listener for delivered ACK
// -----------------------------------------------------------------------------
export function onNotificationDeliveredAck(
  handler: (data: { status?: string }) => void,
) {
  const cb = (data: any) => handler(data ?? {});
  websocketManager.on("notifications:delivered_ack", cb);

  return () => {
    websocketManager.off("notifications:delivered_ack", cb);
  };
}

// -----------------------------------------------------------------------------
// Notify backend that client has received a notification event
// -----------------------------------------------------------------------------
export function markNotificationDelivered(data: Record<string, any> = {}) {
  if (!websocketManager.isConnected()) return;

  websocketManager.sendMessage("notifications", {
    type: "delivered",
    data,
  });
}